// ===========================================
// SERVICE: Tareas para un grupo de clientes (CORE)
// ===========================================

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Prioridad, TareaCompleta } from '@/types/database'
import { fetchAllPages, fetchInChunks, IN_CHUNK_SIZE, splitIntoBatches } from '@/lib/postgrest-chunks'

export type TipoGrupoTarea = 'etiqueta' | 'condicion_iva' | 'impuesto' | 'tipo_persona'

/** Tipos de cliente de `personas.tipo`, con los labels del alta de cliente. */
export const TIPOS_PERSONA_GRUPO = {
  fisica: 'Persona Física',
  sucesion_indivisa: 'Sucesión Indivisa',
  juridica: 'Sociedad',
} as const

export type TipoPersonaGrupo = keyof typeof TIPOS_PERSONA_GRUPO

/**
 * Tarea con las columnas de grupo de `vista_tareas_completas`. `grupo_clave` es la misma para las
 * tareas creadas juntas para un grupo de clientes: las de una ocurrencia de una repetición, o las
 * de un lote de `crearTareaParaGrupo`. Es null en una tarea suelta.
 */
export type TareaConGrupo = TareaCompleta & {
  grupo_id?: string | null
  grupo_clave?: string | null
}

/** Las tareas de un grupo, vistas desde el tablero. */
export interface GrupoDeTablero {
  clave: string
  /** Las tareas del grupo que pasan los filtros del tablero. */
  enTablero: TareaConGrupo[]
  /** Todas las tareas del grupo que el usuario ve, completadas incluidas. */
  visibles: TareaConGrupo[]
}

export type ItemTablero = { columna: string } & (
  | { tipo: 'tarea'; tarea: TareaConGrupo }
  | { tipo: 'grupo'; grupo: GrupoDeTablero }
)

/**
 * Arma los ítems del tablero. Las tareas de un grupo van juntas en un solo ítem cuando el usuario
 * ve dos o más. Con una sola visible queda como tarea suelta.
 *
 * - `tablero` son las tareas que trajo la consulta del tablero, en el orden en que se muestran.
 * - `deGrupos` son las tareas de esos grupos en cualquier estado. El avance del grupo sale de ahí,
 *   porque el tablero no trae las completadas.
 * - `clasificar` devuelve la columna de una tarea. Un grupo va en la columna de sus tareas del
 *   tablero que aparece primero en `ordenColumnas`, que es la más urgente.
 */
export const agruparTareas = (
  tablero: TareaConGrupo[],
  deGrupos: TareaConGrupo[],
  clasificar: (tarea: TareaConGrupo) => string,
  ordenColumnas: readonly string[]
): ItemTablero[] => {
  const enTableroPorClave = new Map<string, TareaConGrupo[]>()
  for (const tarea of tablero) {
    if (!tarea.grupo_clave) continue
    enTableroPorClave.set(tarea.grupo_clave, [...(enTableroPorClave.get(tarea.grupo_clave) ?? []), tarea])
  }

  // La versión del tablero pisa a la de `deGrupos`: es la que el usuario tiene a la vista.
  const visiblesPorClave = new Map<string, Map<string, TareaConGrupo>>()
  for (const tarea of [...deGrupos, ...tablero]) {
    if (!tarea.grupo_clave || !enTableroPorClave.has(tarea.grupo_clave)) continue
    const porId = visiblesPorClave.get(tarea.grupo_clave) ?? new Map<string, TareaConGrupo>()
    porId.set(tarea.id, tarea)
    visiblesPorClave.set(tarea.grupo_clave, porId)
  }

  const posicion = (columna: string) => {
    const indice = ordenColumnas.indexOf(columna)
    return indice === -1 ? ordenColumnas.length : indice
  }

  const items: ItemTablero[] = []
  const clavesAgregadas = new Set<string>()
  for (const tarea of tablero) {
    const clave = tarea.grupo_clave
    const visibles = clave ? [...(visiblesPorClave.get(clave)?.values() ?? [])] : []
    if (!clave || visibles.length < 2) {
      items.push({ tipo: 'tarea', tarea, columna: clasificar(tarea) })
      continue
    }
    if (clavesAgregadas.has(clave)) continue
    clavesAgregadas.add(clave)

    const enTablero = enTableroPorClave.get(clave) ?? [tarea]
    const columna = enTablero
      .map(clasificar)
      .reduce((masUrgente, actual) => (posicion(actual) < posicion(masUrgente) ? actual : masUrgente))
    items.push({ tipo: 'grupo', grupo: { clave, enTablero, visibles }, columna })
  }
  return items
}

/**
 * Grupo de clientes con el formato de las columnas de `tareas_recurrentes` y de los argumentos
 * de `clientes_de_grupo`. A lo sumo una de las cuatro tiene valor.
 */
export interface ColumnasGrupo {
  etiqueta_id: string | null
  condicion_iva: string | null
  impuesto_id: string | null
  tipo_persona: string | null
}

/** Traduce el grupo elegido en el formulario a las columnas de `tareas_recurrentes`. */
export const columnasDeGrupo = (
  tipo: TipoGrupoTarea | null,
  valor: string | null
): ColumnasGrupo => ({
  etiqueta_id: tipo === 'etiqueta' && valor ? valor : null,
  condicion_iva: tipo === 'condicion_iva' && valor ? valor : null,
  impuesto_id: tipo === 'impuesto' && valor ? valor : null,
  tipo_persona: tipo === 'tipo_persona' && valor ? valor : null,
})

export const tieneGrupo = (grupo: ColumnasGrupo): boolean =>
  !!(grupo.etiqueta_id || grupo.condicion_iva || grupo.impuesto_id || grupo.tipo_persona)

const argumentosDeGrupo = (organizacionId: string, grupo: ColumnasGrupo) => ({
  p_organizacion_id: organizacionId,
  p_etiqueta_id: grupo.etiqueta_id,
  p_condicion_iva: grupo.condicion_iva,
  p_impuesto_id: grupo.impuesto_id,
  p_tipo_persona: grupo.tipo_persona,
})

/**
 * Cliente sin el genérico de Database: `database.ts` todavía no conoce las RPC de grupo y no se
 * edita a mano. Se castea el cliente y no el método, porque supabase-js usa `this` adentro de `rpc`.
 */
const sinTipos = (supabase: SupabaseClient<Database>) => supabase as unknown as SupabaseClient

export interface ResultadoGrupoTarea {
  creadas: number
}

export interface CrearTareaGrupoInput {
  titulo: string
  descripcion: string | null
  asignado_a: string | null
  asignado_por: string | null
  prioridad: Prioridad
  fecha_limite: string | null
  workspace: string
  organizacion_id: string
  created_by: string
  tipo: TipoGrupoTarea
  valor: string
}

/**
 * Ids de los clientes activos de un grupo, resueltos por `clientes_de_grupo`, la misma función
 * que usa el cron. Corre con las policies del usuario que llama, así que un colaborador que no ve
 * todos los clientes recibe solo los suyos. Se lee paginado porque PostgREST corta en 1000 filas
 * sin avisar.
 */
export async function listarClientesDeGrupo(
  supabase: SupabaseClient<Database>,
  organizacionId: string,
  grupo: ColumnasGrupo
): Promise<string[]> {
  const filas = await fetchAllPages<{ cliente_id: string }>(async (desde, hasta) => {
    const { data, error } = await sinTipos(supabase)
      .rpc('clientes_de_grupo', argumentosDeGrupo(organizacionId, grupo))
      .order('cliente_id')
      .range(desde, hasta)

    if (error) throw error
    return (data ?? []) as { cliente_id: string }[]
  })

  return filas.map((f) => f.cliente_id)
}

/**
 * Cuántas tareas crea el cron en cada ocurrencia de una repetición de ese grupo. Lo cuenta
 * `contar_clientes_de_grupo` con los privilegios del cron, así que el número es el mismo para
 * cualquier usuario, vea o no a todos los clientes.
 */
export async function contarClientesDeGrupoRecurrente(
  supabase: SupabaseClient<Database>,
  organizacionId: string,
  grupo: ColumnasGrupo
): Promise<number> {
  const { data, error } = await sinTipos(supabase).rpc(
    'contar_clientes_de_grupo',
    argumentosDeGrupo(organizacionId, grupo)
  )

  if (error) throw error
  return Number(data ?? 0)
}

/**
 * Crea una tarea por cada cliente activo de un grupo, en un solo insert por lote.
 *
 * Todo o nada, a diferencia del fan-out de `tareasRecurrentesService`: ahí insertar de a
 * uno tiene sentido porque hay un índice único y el conflicto con el cron es el
 * resultado esperado de una carrera. Acá no hay ni índice ni cron — si el insert falla a
 * mitad de camino y el usuario reintenta, un fan-out con `allSettled` dejaría duplicadas
 * las filas que sí habían entrado. Con el lote, un fallo deja 0 tareas creadas, nunca un
 * éxito parcial mudo.
 */
export async function crearTareaParaGrupo(
  supabase: SupabaseClient<Database>,
  input: CrearTareaGrupoInput
): Promise<ResultadoGrupoTarea> {
  const clientes = await listarClientesDeGrupo(
    supabase,
    input.organizacion_id,
    columnasDeGrupo(input.tipo, input.valor)
  )
  if (clientes.length === 0) return { creadas: 0 }

  // Las tareas del lote comparten `grupo_id`, que es lo que el tablero usa para mostrarlas juntas.
  const grupoId = crypto.randomUUID()
  const filas = clientes.map((clienteId) => ({
    grupo_id: grupoId,
    titulo: input.titulo,
    descripcion: input.descripcion,
    cliente_id: clienteId,
    asignado_a: input.asignado_a,
    asignado_por: input.asignado_por,
    prioridad: input.prioridad,
    estado: 'pendiente' as const,
    fecha_limite: input.fecha_limite,
    workspace: input.workspace,
    organizacion_id: input.organizacion_id,
    created_by: input.created_by,
  }))

  const { error } = await supabase.from('tareas').insert(filas as never)
  if (error) throw error

  return { creadas: filas.length }
}

/**
 * Las tareas de los grupos pedidos, en cualquier estado. Corre con las policies del usuario, así
 * que trae solo las que él ve. Se lee por lotes de claves y paginado, porque PostgREST rechaza una
 * URL larga y corta en 1000 filas sin avisar.
 */
export async function listarTareasDeGrupos(
  supabase: SupabaseClient<Database>,
  claves: string[]
): Promise<TareaConGrupo[]> {
  return fetchInChunks(claves, (lote) =>
    fetchAllPages<TareaConGrupo>(async (desde, hasta) => {
      const { data, error } = await sinTipos(supabase)
        .from('vista_tareas_completas')
        .select('*')
        .in('grupo_clave', lote)
        .order('id')
        .range(desde, hasta)

      if (error) throw error
      return (data ?? []) as TareaConGrupo[]
    })
  )
}

/** Fila devuelta por `actualizarFechaDeTareas`. */
export interface TareaConFechaActualizada {
  id: string
  titulo: string
  asignado_a: string | null
}

/** Estado actual de una tarea en la base, con los datos que deciden si se le puede mover la fecha. */
export interface EstadoDeTarea {
  id: string
  estado: string
  fecha_limite: string | null
  asignado_a: string | null
  created_by: string | null
}

/** Lee de la base el estado actual de las tareas pedidas. */
export async function obtenerEstadoDeTareas(
  supabase: SupabaseClient<Database>,
  ids: string[]
): Promise<EstadoDeTarea[]> {
  return fetchInChunks(ids, async (lote) => {
    const { data, error } = await sinTipos(supabase)
      .from('tareas')
      .select('id, estado, fecha_limite, asignado_a, created_by')
      .in('id', lote)

    if (error) throw error
    return (data ?? []) as EstadoDeTarea[]
  })
}

/** Resultado de `actualizarFechaDeTareas`. `error` es el primer lote que falló, o null. */
export interface FechaDeTareasActualizada {
  actualizadas: TareaConFechaActualizada[]
  error: unknown
}

/**
 * Pone la misma fecha límite a las tareas pedidas. Nunca mueve una tarea completada, aunque venga
 * en `ids`. Corre con las policies del usuario: una tarea fuera de su alcance no se actualiza y no
 * vuelve en la respuesta, y sin permiso de edición en la organización el lote falla con 42501.
 * Se escribe por lotes de ids porque PostgREST rechaza una URL larga. Cada lote se confirma por
 * separado, así que devuelve las tareas de los lotes que se guardaron junto con el error del
 * primero que falló.
 */
export async function actualizarFechaDeTareas(
  supabase: SupabaseClient<Database>,
  ids: string[],
  fecha: string | null
): Promise<FechaDeTareasActualizada> {
  const lotes = await Promise.allSettled(
    splitIntoBatches(ids, IN_CHUNK_SIZE).map(async (lote) => {
      const { data, error } = await sinTipos(supabase)
        .from('tareas')
        .update({ fecha_limite: fecha, updated_at: new Date().toISOString() })
        .in('id', lote)
        .neq('estado', 'completada')
        .select('id, titulo, asignado_a')

      if (error) throw error
      return (data ?? []) as TareaConFechaActualizada[]
    })
  )
  const fallido = lotes.find((l) => l.status === 'rejected')
  return {
    actualizadas: lotes.flatMap((l) => (l.status === 'fulfilled' ? l.value : [])),
    error: fallido?.status === 'rejected' ? fallido.reason : null,
  }
}
